import { startOfDay, endOfDay, getYear, getMonth, getDate, set } from 'date-fns';
import { toDate } from 'date-fns-tz';
import { padNumber } from '../../../components/common/DateRangeTimePicker/helpers';

/**
 * Formats a date string for timezone conversion
 * @param {Date} date - The date to format
 * @param {string} timeString - Time in HH:mm format (e.g., "14:30")
 * @returns {string} Formatted date string for toDate()
 */
const formatDateStringForTimezone = (date, timeString) => {
  const year = getYear(date);
  const month = String(getMonth(date) + 1).padStart(2, '0');
  const day = String(getDate(date)).padStart(2, '0');

  return `${year}-${month}-${day} ${timeString}`;
};

/**
 * Creates a date with timezone awareness
 * @param {Date} date - The base date
 * @param {number} hour - Hour (0-23)
 * @param {number} minute - Minute (0-59)
 * @param {number} second - Second (0-59)
 * @param {string} timezone - IANA timezone identifier
 * @returns {Date} Date object in the specified timezone
 */
export const createTimezoneAwareDate = (date, hour, minute, second, timezone) => {
  const timeString = `${padNumber(hour)}:${padNumber(minute)}:${padNumber(second)}`;
  const dateStr = formatDateStringForTimezone(date, timeString);

  return toDate(dateStr, { timeZone: timezone });
};

/**
 * Parses time string and creates end date with timezone
 * @param {Date} date - The base date
 * @param {string} timeString - Time in HH:mm format
 * @param {string|null} timezone - IANA timezone identifier or null for local time
 * @returns {Date} End date with time set to HH:mm:59.999
 */
export const createEndDateWithTime = (date, timeString, timezone) => {
  const timeParts = timeString.split(':');
  const hour = parseInt(timeParts[0], 10);
  const minute = parseInt(timeParts[1], 10);

  if (timezone) {
    return createTimezoneAwareDate(date, hour, minute, 59, timezone);
  }

  return set(date, {
    hours: hour,
    minutes: minute,
    seconds: 59,
    milliseconds: 999,
  });
};

/**
 * Creates end of day date with timezone awareness
 * @param {Date} date - The base date
 * @param {string|null} timezone - IANA timezone identifier or null for local time
 * @returns {Date} End of day (23:59:59.999)
 */
export const createEndOfDay = (date, timezone) => {
  if (timezone) {
    return createTimezoneAwareDate(date, 23, 59, 59, timezone);
  }

  return endOfDay(date);
};

/**
 * Parses time string and creates start date with timezone
 * @param {Date} date - The base date
 * @param {string} timeString - Time in HH:mm format
 * @param {string|null} timezone - IANA timezone identifier or null for local time
 * @returns {Date} Start date with time set to HH:mm:00.000
 */
export const createStartDateWithTime = (date, timeString, timezone) => {
  const timeParts = timeString.split(':');
  const hour = parseInt(timeParts[0], 10);
  const minute = parseInt(timeParts[1], 10);

  if (timezone) {
    return createTimezoneAwareDate(date, hour, minute, 0, timezone);
  }

  return set(date, {
    hours: hour,
    minutes: minute,
    seconds: 0,
    milliseconds: 0,
  });
};

/**
 * Creates start of day date with timezone awareness
 * @param {Date} date - The base date
 * @param {string|null} timezone - IANA timezone identifier or null for local time
 * @returns {Date} Start of day (00:00:00.000)
 */
export const createStartOfDay = (date, timezone) => {
  if (timezone) {
    return createTimezoneAwareDate(date, 0, 0, 0, timezone);
  }

  return startOfDay(date);
};

/**
 * Gets the browser's default timezone
 * @returns {string} IANA timezone identifier (e.g., "America/New_York")
 */
export const getBrowserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};
